import { useState, useEffect, useCallback } from "react";
import firebaseMessagingService from "../services/firebaseMessaging";
import { toast } from "react-toastify";
import { useDispatch } from "react-redux";
import { fetchNotifications } from "../store/notificationSlice";
import {
  getRouteForNotification,
  navigateToRoute,
} from "../utils/notificationRouting";

// Hook for managing Firebase notifications
export const useFirebaseNotifications = () => {

  const [token, setToken] = useState(null);
  const [permission, setPermission] = useState("default");
  const [isInitialized, setIsInitialized] = useState(false);
  const [error, setError] = useState(null);

  // Safely get dispatch - will be undefined if Redux context is not available
  let dispatch;
  try {
    // eslint-disable-next-line react-hooks/rules-of-hooks
    dispatch = useDispatch();
  } catch (error) {
    dispatch = undefined;
  }

  // Initialize Firebase messaging
  const initialize = useCallback(async () => {
    try {
      setError(null);

      const success = await firebaseMessagingService.initialize();

      if (success) {
        const fcmToken = await firebaseMessagingService.getToken();

        setToken(fcmToken);
        setPermission(firebaseMessagingService.getNotificationPermission());
        setIsInitialized(true);

        // Send token to server
        await firebaseMessagingService.sendTokenToServer(fcmToken);

        return fcmToken;
      } else {
        setError("Failed to initialize Firebase messaging");
        return null;
      }
    } catch (err) {
      setError(err.message);
      return null;
    }
  }, []);

  // Request notification permission
  const requestPermission = useCallback(async () => {
    try {
      const fcmToken = await initialize();
      if (fcmToken) {
        return true;
      } else {
        toast.error("Failed to enable notifications");
        return false;
      }
    } catch (err) {
      toast.error("Error enabling notifications");
      return false;
    }
  }, [initialize]);

  // Handle foreground notifications
  const handleForegroundNotification = useCallback(
    (payload) => {

      // Refresh notifications from server when a new one arrives (if Redux is available)
      if (dispatch) {
        dispatch(fetchNotifications(1));
      }

      // Show browser notification if permission granted
      if (Notification.permission === "granted") {
        const notificationTitle = payload.notification?.title || "VibeLinks";
        const notificationBody =
          payload.notification?.body || "You have a new notification";
        const notificationIcon = payload.notification?.icon || "/logo192.png";

        const notification = new Notification(notificationTitle, {
          body: notificationBody,
          icon: notificationIcon,
          badge: "/logo192.png",
          tag: payload.data?.id || "vibelinks-notification",
          requireInteraction: true,
        });

        // Handle notification click
        notification.onclick = () => {
          window.focus();
          notification.close();

          // Navigate to relevant page if data provided
          const route = getRouteForNotification(payload);
          navigateToRoute(route);
        };

        // Auto close after 10 seconds
        setTimeout(() => {
          notification.close();
        }, 10000);
      }
    },
    [dispatch]
  );

  // Start listening for notifications
  const startListening = useCallback(() => {
    if (isInitialized && token) {
      firebaseMessagingService.startListening(handleForegroundNotification);
    }
  }, [isInitialized, token, handleForegroundNotification]);

  // Stop listening for notifications
  const stopListening = useCallback(() => {
    firebaseMessagingService.stopListening();
  }, []);

  // Refresh FCM token
  const refreshToken = useCallback(async () => {
    try {
      const newToken = await initialize();
      if (newToken) {
        setToken(newToken);
        return newToken;
      }
      return null;
    } catch (err) {
      setError(err.message);
      return null;
    }
  }, [initialize]);

  // Clear token (for logout)
  const clearToken = useCallback(() => {
    firebaseMessagingService.clearToken();
    setToken(null);
    setIsInitialized(false);
    setPermission("default");
    setError(null);
  }, []);

  // Check if notifications are supported
  const isSupported = firebaseMessagingService.isNotificationSupported();

  return {
    token,
    permission,
    isInitialized,
    error,
    isSupported,
    initialize,
    requestPermission,
    startListening,
    stopListening,
    refreshToken,
    clearToken,
  };
};

// Hook for notification permission status
export const useNotificationPermission = () => {
  const [permission, setPermission] = useState("default");

  useEffect(() => {
    if ("Notification" in window) {
      setPermission(Notification.permission);
    } else {
      setPermission("not-supported");
    }
  }, []);

  const requestPermission = async () => {
    if (!("Notification" in window)) {
      return "not-supported";
    }

    try {
      const result = await Notification.requestPermission();
      setPermission(result);
      return result;
    } catch (error) {
      return "denied";
    }
  };

  return { permission, requestPermission };
};
